import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Flag, CheckCircle2, XCircle, Plus, Edit2 } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card.js';
import { Badge } from '../../components/ui/Badge.js';
import { Button } from '../../components/ui/Button.js';
import { Input, SearchInput } from '../../components/ui/Input.js';
import { Modal } from '../../components/ui/Modal.js';
import { TableSkeleton } from '../../components/ui/Skeleton.js';
import { EmptyState, ErrorState } from '../../components/ui/EmptyState.js';
import { PermissionGuard } from '../../components/security/PermissionGuard.js';
import { useToast } from '../../context/ToastContext.js';
import { useI18n } from '../../context/I18nContext.js';
import { fetchFeatureFlags, patchFeatureFlag, createFeatureFlag } from '../../api/features.js';
import { formatDate } from '../../lib/utils.js';
import type { FeatureFlag, DevicePlatform } from '@hisob/types';

const FLAG_META: Record<string, { titleUz: string; descUz: string }> = {
  'core.budgeting': {
    titleUz: 'Asosiy byudjetlashtirish',
    descUz: 'Oylik xarajatlar va daromadlar byudjetini rejalashtirish moduli',
  },
  'core.custom_categories': {
    titleUz: 'Shaxsiy xarajat toifalari',
    descUz: 'Foydalanuvchi tomonidan yangi daromad va xarajat toifalarini yaratish imkoniyati',
  },
  'premium.unlimited_accounts': {
    titleUz: 'Cheksiz hisoblar va kartalar (Premium)',
    descUz: 'Barcha bank kartalari, hamyonlar va omonat hisoblarini cheklovlarsiz boshqarish imkoniyati',
  },
  'premium.advanced_statistics': {
    titleUz: 'Kengaytirilgan tahlil va statistika',
    descUz: 'Chuqurlashtirilgan grafiklar, xarajatlar dinamikasi va taqqoslash hisoboti',
  },
  'premium.export': {
    titleUz: 'Ma\'lumotlarni eksport qilish',
    descUz: 'Barcha moliyaviy yozuvlarni Excel, CSV va PDF formatlarida yuklab olish',
  },
  'premium.ai_insights': {
    titleUz: 'AI moliyaviy maslahatchi',
    descUz: 'Sun\'iy intellekt asosida xarajatlarni tahlil qilish va tejamkorlik tavsiyalari',
  },
  'premium.recurring_transactions': {
    titleUz: 'Avtomatik takrorlanuvchi to\'lovlar',
    descUz: 'Kommunal to\'lovlar, obunalar va doimiy to\'lovlarni avtomatik rejalashtirish',
  },
  'premium.multi_currency': {
    titleUz: 'Ko\'p valyutali hisoblar',
    descUz: 'UZS, USD, EUR va boshqa valyutalarda parallel hisob-kitoblar va kurslar',
  },
};

export function FeatureFlagsPage() {
  const [search, setSearch] = useState('');
  const [editingFlag, setEditingFlag] = useState<FeatureFlag | null>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

  // Edit form state
  const [enabled, setEnabled] = useState(false);
  const [platform, setPlatform] = useState<DevicePlatform | 'all'>('all');
  const [minVersion, setMinVersion] = useState('');
  const [rolloutPercentage, setRolloutPercentage] = useState(100);
  const [premiumOnly, setPremiumOnly] = useState(false);
  const [configJsonRaw, setConfigJsonRaw] = useState('');

  // Create form state
  const [newKey, setNewKey] = useState('');

  const queryClient = useQueryClient();
  const { success, error: toastError } = useToast();
  const { t } = useI18n();

  const { data: flags, isLoading, error, refetch } = useQuery({
    queryKey: ['admin-features'],
    queryFn: fetchFeatureFlags,
  });

  const openEdit = (flag: FeatureFlag) => {
    setEditingFlag(flag);
    setEnabled(flag.enabled);
    setPlatform(flag.platform || 'all');
    setMinVersion(flag.minimum_app_version || '');
    setRolloutPercentage(flag.rollout_percentage);
    setPremiumOnly(flag.premium_only);
    setConfigJsonRaw(flag.config_json ? JSON.stringify(flag.config_json, null, 2) : '');
  };

  const patchMutation = useMutation({
    mutationFn: () => {
      let parsedJson = null;
      if (configJsonRaw.trim()) {
        try {
          parsedJson = JSON.parse(configJsonRaw);
        } catch {
          throw new Error(t('features.jsonMalformedError'));
        }
      }
      return patchFeatureFlag(editingFlag!.key, {
        enabled,
        platform: platform === 'all' ? 'all' : platform,
        minimum_app_version: minVersion.trim() || null,
        rollout_percentage: rolloutPercentage,
        premium_only: premiumOnly,
        config_json: parsedJson,
      });
    },
    onSuccess: () => {
      success(t('features.saveSuccess'));
      setEditingFlag(null);
      queryClient.invalidateQueries({ queryKey: ['admin-features'] });
      queryClient.invalidateQueries({ queryKey: ['overview-metrics'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to update feature flag', err.requestId);
    },
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createFeatureFlag({
        key: newKey.trim(),
        enabled: false,
        rollout_percentage: 100,
        premium_only: false,
      }),
    onSuccess: () => {
      success(t('features.createSuccess'));
      setIsCreateModalOpen(false);
      setNewKey('');
      queryClient.invalidateQueries({ queryKey: ['admin-features'] });
    },
    onError: (err: any) => {
      toastError(err.message || 'Failed to create feature flag', err.requestId);
    },
  });

  const filteredFlags = (flags || []).filter((f) => {
    if (!search.trim()) return true;
    return f.key.toLowerCase().includes(search.toLowerCase());
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-xl font-bold tracking-tight text-slate-900 dark:text-slate-100 flex items-center gap-2">
            <Flag className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
            {t('features.title')}
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
            {t('features.description')}
          </p>
        </div>

        <PermissionGuard permission="features:write">
          <Button size="sm" variant="primary" onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="w-4 h-4 mr-1.5" />
            {t('features.newFlagBtn')}
          </Button>
        </PermissionGuard>
      </div>

      {/* Filter Bar */}
      <Card>
        <CardContent className="p-4">
          <div className="w-full sm:w-80">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={t('features.searchPlaceholder')}
            />
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0 overflow-x-auto">
          {isLoading ? (
            <div className="p-6">
              <TableSkeleton rows={5} cols={7} />
            </div>
          ) : error ? (
            <div className="p-6">
              <ErrorState title={t('features.loadError')} message={(error as any).message} onRetry={() => refetch()} />
            </div>
          ) : filteredFlags.length === 0 ? (
            <div className="p-6">
              <EmptyState
                title={t('features.emptyTitle')}
                description={t('features.emptyDesc')}
              />
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead className="bg-slate-50 dark:bg-slate-900/60 border-b border-slate-200 dark:border-slate-800 text-slate-500 font-semibold uppercase tracking-wider">
                <tr>
                  <th className="py-3 px-4">{t('features.tableKey')}</th>
                  <th className="py-3 px-4">{t('features.tableStatus')}</th>
                  <th className="py-3 px-4">{t('features.tablePlatform')}</th>
                  <th className="py-3 px-4">{t('features.tableMinVersion')}</th>
                  <th className="py-3 px-4">{t('features.tableRollout')}</th>
                  <th className="py-3 px-4">{t('features.tablePremiumOnly')}</th>
                  <th className="py-3 px-4">Last Updated</th>
                  <th className="py-3 px-4 text-right">{t('features.tableActions')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {filteredFlags.map((flag) => (
                  <tr key={flag.key} className="hover:bg-slate-50 dark:hover:bg-slate-900/40">
                    <td className="py-3 px-4">
                      <div className="font-mono font-semibold text-slate-900 dark:text-slate-100">
                        {flag.key}
                      </div>
                      {FLAG_META[flag.key] && (
                        <div className="text-[11px] text-slate-500 font-sans mt-0.5">
                          {FLAG_META[flag.key].titleUz}
                        </div>
                      )}
                    </td>
                    <td className="py-3 px-4">
                      {flag.enabled ? (
                        <Badge variant="success">
                          <CheckCircle2 className="w-3 h-3 text-emerald-500" /> {t('common.active')}
                        </Badge>
                      ) : (
                        <Badge variant="neutral">
                          <XCircle className="w-3 h-3 text-slate-400" /> {t('common.inactive')}
                        </Badge>
                      )}
                    </td>
                    <td className="py-3 px-4 uppercase text-[11px] font-mono text-slate-500">
                      {flag.platform || 'all'}
                    </td>
                    <td className="py-3 px-4 font-mono text-slate-600 dark:text-slate-400">
                      {flag.minimum_app_version ? `v${flag.minimum_app_version}` : 'Any'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-2">
                        <div className="w-16 h-2 bg-slate-200 dark:bg-slate-800 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-indigo-600 rounded-full"
                            style={{ width: `${flag.rollout_percentage}%` }}
                          />
                        </div>
                        <span className="font-mono text-xs">{flag.rollout_percentage}%</span>
                      </div>
                    </td>
                    <td className="py-3 px-4">
                      {flag.premium_only ? (
                        <Badge variant="warning">{t('features.tablePremiumOnly')}</Badge>
                      ) : (
                        <span className="text-slate-400">{t('features.freeTier')}</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-slate-500">
                      {formatDate(flag.updated_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <PermissionGuard permission="features:write">
                        <Button size="sm" variant="outline" onClick={() => openEdit(flag)}>
                          <Edit2 className="w-3 h-3 mr-1" /> {t('common.edit')}
                        </Button>
                      </PermissionGuard>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {/* Edit Drawer / Modal */}
      {editingFlag && (
        <Modal
          isOpen={Boolean(editingFlag)}
          onClose={() => setEditingFlag(null)}
          title={t('features.editModalTitle', { key: editingFlag.key })}
          maxWidth="lg"
        >
          <div className="space-y-4 text-xs">
            {/* Feature Description Card */}
            {FLAG_META[editingFlag.key] && (
              <div className="p-3 rounded-xl bg-indigo-50/70 dark:bg-indigo-950/30 border border-indigo-200 dark:border-indigo-900/50">
                <div className="font-semibold text-indigo-950 dark:text-indigo-200 text-xs">
                  {FLAG_META[editingFlag.key].titleUz}
                </div>
                <div className="text-[11px] text-slate-600 dark:text-slate-400 mt-0.5 leading-relaxed">
                  {FLAG_META[editingFlag.key].descUz}
                </div>
              </div>
            )}

            {/* Enable/Disable Toggle */}
            <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                  {t('features.enabledLabel')}
                </span>
                <span className="text-[11px] text-slate-500">
                  {t('features.enabledHelp')}
                </span>
              </div>
              <input
                type="checkbox"
                checked={enabled}
                onChange={(e) => setEnabled(e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
              />
            </div>

            {/* Platform & Min Version */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-slate-300">{t('features.platformLabel')}</label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value as any)}
                  className="w-full h-9 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 px-3 text-xs"
                >
                  <option value="all">{t('features.platformAll')}</option>
                  <option value="ios">{t('features.platformIos')}</option>
                  <option value="android">{t('features.platformAndroid')}</option>
                  <option value="web">{t('features.platformWeb')}</option>
                </select>
              </div>

              <div className="space-y-1">
                <label className="font-semibold text-slate-700 dark:text-slate-300">{t('features.minVersionLabel')}</label>
                <Input
                  value={minVersion}
                  onChange={(e) => setMinVersion(e.target.value)}
                  placeholder={t('features.minVersionPlaceholder')}
                  className="font-mono text-xs"
                />
              </div>
            </div>

            {/* Rollout Percentage Slider */}
            <div className="space-y-2 p-3 rounded-xl border border-slate-200 dark:border-slate-800">
              <div className="flex items-center justify-between">
                <label className="font-semibold text-slate-700 dark:text-slate-300">
                  {t('features.rolloutLabel')}
                </label>
                <span className="font-mono font-bold text-sm text-indigo-600 dark:text-indigo-400">
                  {rolloutPercentage}%
                </span>
              </div>
              <input
                type="range"
                min="0"
                max="100"
                step="5"
                value={rolloutPercentage}
                onChange={(e) => setRolloutPercentage(parseInt(e.target.value, 10))}
                className="w-full accent-indigo-600"
              />
              <p className="text-[11px] text-slate-400">
                {t('features.rolloutHelp')}
              </p>
            </div>

            {/* Premium Only Toggle */}
            <div className="p-3 rounded-xl border border-slate-200 dark:border-slate-800 flex items-center justify-between">
              <div>
                <span className="font-semibold text-slate-900 dark:text-slate-100 block">
                  {t('features.premiumOnlyLabel')}
                </span>
                <span className="text-[11px] text-slate-500">
                  {t('features.premiumOnlyHelp')}
                </span>
              </div>
              <input
                type="checkbox"
                checked={premiumOnly}
                onChange={(e) => setPremiumOnly(e.target.checked)}
                className="w-5 h-5 rounded text-indigo-600 focus:ring-indigo-500"
              />
            </div>

            {/* Config JSON Payload */}
            <div className="space-y-1">
              <label className="font-semibold text-slate-700 dark:text-slate-300">
                {t('features.configJsonLabel')}
              </label>
              <textarea
                rows={4}
                value={configJsonRaw}
                onChange={(e) => setConfigJsonRaw(e.target.value)}
                placeholder='{"max_items": 10, "enable_v2": true}'
                className="w-full p-2.5 font-mono text-xs rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 focus:ring-2 focus:ring-indigo-500"
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
              <Button variant="ghost" onClick={() => setEditingFlag(null)}>
                {t('common.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={() => patchMutation.mutate()}
                isLoading={patchMutation.isPending}
              >
                {t('features.saveBtn')}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Create Flag Modal */}
      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title={t('features.createModalTitle')}
        maxWidth="md"
      >
        <div className="space-y-4 text-xs">
          <div className="space-y-1">
            <label className="font-semibold text-slate-700 dark:text-slate-300">
              {t('features.flagKeyLabel')} <span className="text-rose-500">*</span>
            </label>
            <Input
              value={newKey}
              onChange={(e) => setNewKey(e.target.value)}
              placeholder={t('features.flagKeyPlaceholder')}
              className="font-mono text-xs"
            />
          </div>

          <div className="flex justify-end gap-2 pt-3 border-t border-slate-100 dark:border-slate-800">
            <Button variant="ghost" onClick={() => setIsCreateModalOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={!newKey.trim() || createMutation.isPending}
              isLoading={createMutation.isPending}
              onClick={() => createMutation.mutate()}
            >
              {t('features.createBtn')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
